import { ArrowRight, Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CANVAS_FIGMA_SCALE, cubicChainWalker, type CanvasLine } from '../../../../lib/home-map/canvas-layout';
import { AGENT_RING_R, WORLD_CENTER } from '../../../../lib/home-map/map-layout';
import styles from './CanvasDemoFlow.module.css';

type Pt = { x: number; y: number };

/** Where the loop draws, as of the last render: the camera (world → layer px) and the entry lines on the layer. */
export interface DemoFlowGeo {
  project: (p: Pt) => Pt;
  /** Layer px per world unit. */
  k: number;
  /** Each entry line's cubic, in layer px, by its entry key. */
  entryPoints: Record<string, Pt[]>;
  /** The agents' centres in the world as they stand NOW (the orbit turns them), and their radius. */
  agents: () => Pt[];
  agentR: number;
  /** Every channel and tool plate's centre in the world, and their radius — nothing of the loop's paints over one. */
  plates: () => Pt[];
  plateR: number;
}

export interface DemoFlowChannel {
  id: string;
  label: string;
  slug: string;
  /** The plate's centre, in the world. */
  centre: Pt;
  line: CanvasLine;
}

export interface DemoFlowCategory {
  key: string;
  label: string;
  /** The entry line's colour — what a delivered tag turns. */
  color: string;
}

// ## The prototype's defaults ("Symphony Live", the octopus): every number here is its own, in FRAME px at 100%
const TAGS_PER_WAVE = 7;
const ICONS_PER_WAVE = 4;
const WAVE_STAGGER_S = 0.08;
const WAVE_EVERY_S = 0.6;
/** A new wave waits until at most this many are still on their way in. */
const REFILL = 2;
const RELEASE_EVERY_S = 0.55;
const SPEED = 110;
const TRAIL = 90;
/** A tag folds into a dot this far before the ring. */
const COLLAPSE_AT = 34;
/** A tag's room on its line when a wave is laid out: a label tag's, an icon's. */
const ROOM = { tag: 96, mini: 20 } as const;
const CONNECTOR_CHANCE = 0.6;
/** The layer is masked to the ring's OUTSIDE: a tag is gone the moment it crosses into the ring and reappears
 *  as it leaves — the prototype's mask, transparent to 127.6 of its 128.1 ring, solid from 128.6. */
const MASK = { inner: 127.6 / 128.1, outer: 128.6 / 128.1 } as const;

type Phase = 'wait' | 'birth' | 'in' | 'sink' | 'inside' | 'emerge' | 'out' | 'absorb';

interface Token {
  id: number;
  mini: boolean;
  ch: DemoFlowChannel;
  cat: DemoFlowCategory;
  agentId: string | null;
  phase: Phase;
  t0: number;
  wait: number;
  /** Measured once mounted, in frame px. */
  wIn: number;
  wOut: number;
  w: number;
  collapsed: boolean;
  release: boolean;
  /** A move's two ends and the entry point's art, in the WORLD — projected every frame, so a tag between two
   *  points stays on them while the camera pans and zooms. */
  from?: Pt;
  to?: Pt;
  art?: Pt;
  trail?: SVGPathElement;
  /** The trail as last drawn, in layer px — what it fades out from when the tag lets go of it. */
  trailPts?: Pt[];
}

interface TagView {
  id: number;
  mini: boolean;
  ch: DemoFlowChannel;
  cat: DemoFlowCategory;
}

/**
 * The octopus's LIFE on the canvas: made-up activity on a loop, ported from the "Symphony Live" prototype.
 * It shows what the map is FOR — things arriving on a channel, an agent picking them up, the result landing
 * in a category — and is deliberately not the account's real activity: it never reads an event, never moves
 * a number, and never stops.
 *
 * Every wave sends grey "→ Channel" tags (and a few bare channel marks) out of the account's own channel
 * plates and along their lines into the ring, where they fold into dots and wait their turn. One at a time
 * they come out the other side as the category's colour — "✓ Tasks" — ride that entry line, and slide into
 * the entry point, which pulses. The plate, the agent that picks one up, the Maestro and (sometimes) a tool
 * pulse as it passes.
 *
 * It runs imperatively off `requestAnimationFrame` on the canvas's screen layer: React renders a tag once,
 * when it is born, and the frame loop moves it. Positions come from the live camera (`geoRef`), so a tag
 * stays on its line as the map pans and zooms, at the frame's speed and size at 100%.
 */
export function CanvasDemoFlow({
  layer,
  geoRef,
  channels,
  categories,
  agentIds,
  connectorIds,
  connectorsLine,
  maestroId,
  pulse,
  glyphFor,
  paused = false,
}: {
  layer: HTMLElement;
  geoRef: RefObject<DemoFlowGeo | null>;
  channels: DemoFlowChannel[];
  categories: DemoFlowCategory[];
  agentIds: string[];
  connectorIds: string[];
  connectorsLine: CanvasLine | null;
  maestroId: string;
  /** Pulse a map node by id — the keyframes are its scale. Returns the animation, so a pause can hold it. */
  pulse: (nodeId: string, scale: number[], ms: number) => Animation | undefined;
  /** A channel's glyph, for the bare marks. */
  glyphFor: (slug: string) => ReactNode;
  /** The canvas's player is paused: the loop's time stands still, and so does every tag. */
  paused?: boolean;
}) {
  const [tags, setTags] = useState<TagView[]>([]);
  const els = useRef(new Map<number, HTMLDivElement>());
  const tokens = useRef<Token[]>([]);
  const trailsRef = useRef<SVGSVGElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  // What is mid-animation holds too: on the layer, and the pulses the loop started outside it (a node's, an entry
  // point's), which it keeps in `pulses` until they finish.
  const pulses = useRef(new Set<Animation>());
  useEffect(() => {
    for (const a of [...(layerRef.current?.getAnimations({ subtree: true }) ?? []), ...pulses.current]) {
      if (paused) a.pause();
      else a.play();
    }
  }, [paused]);
  // The inputs the loop reads, refreshed every render without restarting it.
  const live = useRef({ channels, categories, agentIds, connectorIds, connectorsLine, maestroId, pulse });
  live.current = { channels, categories, agentIds, connectorIds, connectorsLine, maestroId, pulse };

  // One ref callback per tag, kept for its life: a fresh one each render would have React detach and re-attach
  // it, and the re-measure would undo the width the loop has since given the tag.
  const refs = useRef(new Map<number, (el: HTMLDivElement | null) => void>());
  const register = useCallback((id: number) => {
    let cb = refs.current.get(id);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        if (!el) {
          els.current.delete(id);
          refs.current.delete(id);
          return;
        }
        els.current.set(id, el);
      };
      refs.current.set(id, cb);
    }
    return cb;
  }, []);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let simT = 0;
    // The camera as of this frame, and the trails fading out behind tags that have let go of them — held in the
    // WORLD and redrawn every frame, so a fading trail stays on its line through a pan (and through a pause).
    let geo: DemoFlowGeo | null = null;
    const fading: { path: SVGPathElement; pts: Pt[]; t0: number }[] = [];
    const TRAIL_FADE_S = 0.35;
    const worldOf = (g: DemoFlowGeo, p: Pt): Pt => {
      const o = g.project({ x: 0, y: 0 });
      return { x: (p.x - o.x) / g.k, y: (p.y - o.y) / g.k };
    };
    const drawTrail = (path: SVGPathElement, pts: Pt[], z: number) => {
      path.setAttribute('d', pts.map((q, i) => `${i ? 'L' : 'M'} ${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' '));
      path.style.strokeWidth = `${(5 * z).toFixed(2)}`;
    };
    let nextWave = 0.3;
    let nextRelease = 0;
    let nextId = 0;
    // The channel walkers are in the WORLD, so they are built once per line.
    const walkers = new Map<string, ReturnType<typeof cubicChainWalker>>();
    const walkerOf = (line: CanvasLine) => {
      let w = walkers.get(line.id);
      if (!w) {
        w = cubicChainWalker(line.points);
        walkers.set(line.id, w);
      }
      return w;
    };
    const pick = <T,>(xs: T[]): T | null => (xs.length ? xs[Math.floor(Math.random() * xs.length)] : null);

    const elOf = (t: Token) => els.current.get(t.id);
    const cardOf = (t: Token) => elOf(t)?.querySelector<HTMLElement>(`.${styles.card}`) ?? null;
    const setWidth = (t: Token, w: number) => {
      t.w = w;
      const card = cardOf(t);
      if (card) card.style.width = `${w}px`;
    };
    const place = (t: Token, p: Pt, z: number) => {
      const el = elOf(t);
      if (el) el.style.transform = `translate(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px) scale(${z.toFixed(4)})`;
    };
    // A face's width, measured when the tag first needs it (by then the type has loaded), so the card always
    // holds its whole label.
    // A face's whole width, so the card always holds its full label — never wrapped, never cut. Measured
    // exactly (`offsetWidth` rounds to whole px, sometimes down, which clipped the last letter), back out of
    // the tag's camera scale, rounded up, plus a pixel to spare.
    const faceWidth = (t: Token, face: string, z: number) => {
      if (t.mini) return 20;
      const f = elOf(t)?.querySelector<HTMLElement>(`.${face}`);
      if (!f) return 20;
      return Math.ceil(f.getBoundingClientRect().width / (z || 1)) + 1;
    };
    const clip = (t: Token, css: string) => {
      const card = cardOf(t);
      if (card) card.style.clipPath = css;
    };

    // The glowing run behind a tag on its line: the last TRAIL px of it, in the line's colour AND opacity (the
    // email and WhatsApp lines are their tokens at 0.32 and 0.55).
    const trailStart = (t: Token, color: string, opacity = 1) => {
      const svg = trailsRef.current;
      if (!svg) return;
      const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('class', styles.trail);
      p.style.stroke = color;
      p.style.strokeOpacity = String(opacity);
      svg.appendChild(p);
      t.trail = p;
    };
    const trailAt = (t: Token, along: (d: number) => Pt, head: number, z: number) => {
      if (!t.trail) return;
      const tail = Math.max(0, head - TRAIL);
      const pts: Pt[] = [];
      for (let i = 0; i <= 10; i++) pts.push(along(tail + ((head - tail) * i) / 10));
      drawTrail(t.trail, pts, z);
      t.trailPts = pts;
    };
    const trailEnd = (t: Token) => {
      const p = t.trail;
      const g = geo;
      t.trail = undefined;
      if (!p) return;
      if (g && t.trailPts) fading.push({ path: p, pts: t.trailPts.map((q) => worldOf(g, q)), t0: simT });
      else p.remove();
    };

    // A pulse the loop starts, kept until it finishes so a pause can hold it.
    const track = (a: Animation | undefined) => {
      if (!a) return;
      pulses.current.add(a);
      const drop = () => pulses.current.delete(a);
      a.addEventListener('finish', drop);
      a.addEventListener('cancel', drop);
      if (pausedRef.current) a.pause();
    };
    const pulse = (id: string, scale: number[], ms: number) => track(live.current.pulse(id, scale, ms));
    // The connectors line's glows in flight, and the tool pulses they end in — both on the loop's own clock
    // (`simT`), so a pause holds them where they are, and the frame loop below runs them.
    const glows: { fake: Token; walk: ReturnType<typeof cubicChainWalker>; t0: number; z: number }[] = [];
    const later: { at: number; run: () => void }[] = [];

    // The connectors line lights up and a tool pulses — a tool the agent used.
    const connectorPulse = (z0: number) => {
      const { connectorIds: ids, connectorsLine: line } = live.current;
      const tool = pick(ids);
      if (!tool || !line) return;
      const svg = trailsRef.current;
      if (svg && geoRef.current) {
        const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        p.setAttribute('class', styles.trail);
        p.style.stroke = line.color;
        p.style.strokeOpacity = String(line.opacity);
        svg.appendChild(p);
        glows.push({ fake: { trail: p } as Token, walk: walkerOf(line), t0: simT, z: z0 });
      }
      later.push({ at: simT + 0.6, run: () => pulse(tool, [1, 1.14, 1], 500) });
    };

    const spawnWave = () => {
      const { channels: chs, categories: cats, agentIds: ags } = live.current;
      if (!chs.length || !cats.length) return;
      const kinds = [...Array<boolean>(TAGS_PER_WAVE).fill(false), ...Array<boolean>(ICONS_PER_WAVE).fill(true)];
      for (let i = kinds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [kinds[i], kinds[j]] = [kinds[j], kinds[i]];
      }
      // Laid out so two on one line never overlap: each waits for the one ahead of it on its line to clear.
      const lastOn: Record<string, { t: number; w: number }> = {};
      const born: TagView[] = [];
      kinds.forEach((mini, i) => {
        const ch = pick(chs)!;
        const cat = pick(cats)!;
        const w = mini ? ROOM.mini : ROOM.tag;
        const prev = lastOn[ch.id];
        const minT = prev ? prev.t + (prev.w / 2 + w / 2 + 10) / SPEED : 0;
        const wait = Math.max(i * WAVE_STAGGER_S, minT);
        lastOn[ch.id] = { t: wait, w };
        const tok: Token = {
          id: ++nextId,
          mini,
          ch,
          cat,
          agentId: pick(ags),
          phase: 'wait',
          t0: simT,
          wait,
          wIn: 20,
          wOut: 20,
          w: 20,
          collapsed: mini,
          release: false,
        };
        tokens.current.push(tok);
        born.push({ id: tok.id, mini, ch, cat });
      });
      setTags((prev) => [...prev, ...born]);
    };

    // A category's lane is free once the last tag into it is a width clear of its start.
    const laneFree = (t: Token) =>
      !tokens.current.some(
        (o) => o !== t && o.cat.key === t.cat.key && (o.phase === 'emerge' || (o.phase === 'out' && (simT - o.t0) * SPEED < o.w + 18)),
      );

    const artCentre = (key: string): Pt | null => {
      const art = layer.parentElement?.querySelector<HTMLElement>(`[data-entry-point="${key}"]`);
      if (!art) return null;
      const a = art.getBoundingClientRect();
      const b = layer.getBoundingClientRect();
      return { x: a.left + a.width / 2 - b.left, y: a.top + a.height / 2 - b.top };
    };
    const pulseArt = (key: string, scale: number[], ms: number) => {
      const art = layer.parentElement?.querySelector<HTMLElement>(`[data-entry-point="${key}"]`);
      track(
        art?.animate(
          scale.map((s) => ({ transform: `scale(${s})` })),
          { duration: ms, easing: 'ease-out' },
        ),
      );
    };

    const step = (t: Token, g: DemoFlowGeo, z: number) => {
      const T = simT - t.t0;
      const el = elOf(t);
      if (!el) return;
      const maestro = g.project(WORLD_CENTER);
      // Layer px back into the world — the inverse of the camera, for a point the loop holds across frames.
      const origin = g.project({ x: 0, y: 0 });
      const toWorld = (p: Pt): Pt => ({ x: (p.x - origin.x) / g.k, y: (p.y - origin.y) / g.k });
      const inward = (p: Pt, dist: number): Pt => {
        const dx = maestro.x - p.x;
        const dy = maestro.y - p.y;
        const L = Math.hypot(dx, dy) || 1;
        return { x: p.x + (dx / L) * dist, y: p.y + (dy / L) * dist };
      };
      const walker = walkerOf(t.ch.line);
      const alongIn = (d: number) => g.project(walker.at(d * CANVAS_FIGMA_SCALE));
      const inLen = walker.length / CANVAS_FIGMA_SCALE;

      switch (t.phase) {
        case 'wait':
          if (T >= t.wait) {
            t.phase = 'birth';
            t.t0 = simT;
            t.wIn = faceWidth(t, styles.front, 1);
            setWidth(t, t.wIn);
            el.classList.add(styles.shown);
            pulse(t.ch.id, [1, 0.84, 1.12, 1], 700);
          }
          break;
        case 'birth': {
          // Out of the plate to the line's start, hidden where it still overlaps the plate.
          const ic = g.project(t.ch.centre);
          const p0 = alongIn(0);
          const dist = Math.hypot(p0.x - ic.x, p0.y - ic.y) / z;
          const d = Math.max(0.15, dist / SPEED);
          const k = Math.min(1, T / d);
          const p = { x: ic.x + (p0.x - ic.x) * k, y: ic.y + (p0.y - ic.y) * k };
          place(t, p, z);
          const hid = (ic.x - (p.x - (t.w * z) / 2)) / z;
          clip(t, hid > 0 ? `inset(-6px -6px -6px ${hid.toFixed(1)}px)` : '');
          if (T >= d) {
            clip(t, '');
            t.phase = 'in';
            t.t0 = simT;
            trailStart(t, t.ch.line.color, t.ch.line.opacity);
          }
          break;
        }
        case 'in': {
          const head = Math.min(inLen, T * SPEED);
          place(t, alongIn(head), z);
          trailAt(t, alongIn, head, z);
          if (!t.collapsed && inLen - head < COLLAPSE_AT) {
            t.collapsed = true;
            el.classList.add(styles.collapsed);
            setWidth(t, 20);
          }
          if (head >= inLen) {
            trailEnd(t);
            t.phase = 'sink';
            t.t0 = simT;
            t.from = walker.at(walker.length);
            if (t.agentId) pulse(t.agentId, t.mini ? [1, 1.15, 1] : [1, 1.3, 1], t.mini ? 400 : 700);
            if (!t.mini) pulse(live.current.maestroId, [1, 1.08, 1], 600);
          }
          break;
        }
        case 'sink': {
          const dist = t.w / 2 + 30;
          const k = Math.min(1, T / (dist / SPEED));
          place(t, inward(g.project(t.from!), dist * k * z), z);
          if (k >= 1) {
            t.phase = 'inside';
            t.t0 = simT;
            if (!t.mini && Math.random() < CONNECTOR_CHANCE) connectorPulse(z);
          }
          break;
        }
        case 'inside':
          // Waiting in the ring, where it sank to — held there through the camera, or a pan would leave it
          // outside the ring's mask, afloat.
          if (!t.release) place(t, inward(g.project(t.from!), (t.w / 2 + 30) * z), z);
          else {
            const pts = g.entryPoints[t.cat.key];
            if (!pts) {
              t.phase = 'absorb';
              break;
            }
            // Out the far side as the category: its colour, its name, its width — at once, as the prototype's
            // tag does, so the new label is never cropped while the card widens to it.
            t.phase = 'emerge';
            t.t0 = simT;
            t.wOut = faceWidth(t, styles.back, z);
            el.classList.add(styles.identity);
            el.style.setProperty('--demo-color', t.cat.color);
            setWidth(t, t.wOut);
            const from = inward(pts[0], (t.wOut / 2 + 30) * z);
            t.from = toWorld(from);
            place(t, from, z);
          }
          break;
        case 'emerge': {
          const pts = g.entryPoints[t.cat.key];
          if (pts) t.to = toWorld(pts[0]);
          if (!t.to) {
            t.phase = 'absorb';
            break;
          }
          const from = g.project(t.from!);
          const to = g.project(t.to);
          const dist = Math.hypot(to.x - from.x, to.y - from.y) / z;
          const d = Math.max(0.05, dist / SPEED);
          const k = Math.min(1, T / d);
          place(t, { x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k }, z);
          if (k >= 1) {
            t.phase = 'out';
            t.t0 = simT;
            trailStart(t, t.cat.color);
          }
          break;
        }
        case 'out': {
          const pts = g.entryPoints[t.cat.key];
          if (!pts) {
            t.phase = 'absorb';
            break;
          }
          const w = cubicChainWalker(pts, 24);
          const len = w.length / z;
          const head = Math.min(len, T * SPEED);
          const along = (d: number) => w.at(d * z);
          place(t, along(head), z);
          trailAt(t, along, head, z);
          if (head >= len) {
            trailEnd(t);
            t.phase = 'absorb';
            t.t0 = simT;
            const end = along(len);
            const c = artCentre(t.cat.key);
            t.from = toWorld(end);
            t.art = c ? toWorld(c) : undefined;
            t.to = c ? toWorld({ x: c.x + (t.w * z) / 2 + 6, y: c.y }) : t.from;
          }
          break;
        }
        case 'absorb': {
          // Into the entry point: the tag slides on and is hidden by it.
          if (!t.from || !t.to) {
            kill(t);
            break;
          }
          const from = g.project(t.from);
          const to = g.project(t.to);
          const dist = Math.hypot(to.x - from.x, to.y - from.y) / z;
          const d = Math.max(0.05, dist / SPEED);
          const k = Math.min(1, T / d);
          const p = { x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k };
          place(t, p, z);
          if (t.art) {
            const hid = (p.x + (t.w * z) / 2 - g.project(t.art).x) / z;
            clip(t, hid > 0 ? `inset(-6px ${Math.min(t.w, hid).toFixed(1)}px -6px -6px)` : '');
          }
          if (k >= 1) {
            pulseArt(t.cat.key, t.mini ? [1, 1.08, 1] : [1, 1.18, 1], t.mini ? 400 : 500);
            kill(t);
          }
          break;
        }
      }
    };

    const dead = new Set<number>();
    const kill = (t: Token) => {
      if (t.trail) trailEnd(t);
      dead.add(t.id);
    };

    const tick = (now: number) => {
      // Paused, no time passes — but the frame still runs, so a tag stays on its line as the map pans.
      const dt = pausedRef.current ? 0 : Math.min(0.05, (now - last) / 1000);
      last = now;
      const g = geoRef.current;
      geo = g;
      if (g) {
        // The mask follows the camera: the ring's centre and radius, on the layer — and a hole for every agent,
        // where it stands this frame, so the agents (which reach past the ring) read as above the tags, as the
        // prototype's orbit sits above its tokens.
        const hole = (p: Pt, inner: number, outer: number) =>
          `radial-gradient(circle at ${p.x.toFixed(1)}px ${p.y.toFixed(1)}px, transparent ${inner.toFixed(1)}px, var(--ds-black) ${outer.toFixed(1)}px)`;
        const r = AGENT_RING_R * g.k;
        const ar = g.agentR * g.k;
        const pr = g.plateR * g.k;
        const mask = [
          hole(g.project(WORLD_CENTER), r * MASK.inner, r * MASK.outer),
          ...g.agents().map((a) => hole(g.project(a), ar, ar + 1)),
          // The plates too, so a tag leaves its channel from UNDER it, and no trail ever crosses a plate.
          ...g.plates().map((p) => hole(g.project(p), pr, pr + 1)),
        ].join(', ');
        const el = layerRef.current;
        if (el && el.dataset.mask !== mask) {
          el.dataset.mask = mask;
          el.style.maskImage = mask;
          el.style.setProperty('-webkit-mask-image', mask);
        }
        simT += dt;
        // 1 at the canvas's 100%: the frame's sizes and speed scale with the camera.
        const z = g.k * CANVAS_FIGMA_SCALE;
        const all = tokens.current;
        const pending = all.filter((t) => t.phase === 'wait' || t.phase === 'birth' || t.phase === 'in' || t.phase === 'sink' || t.phase === 'inside').length;
        if (simT >= nextWave && pending <= REFILL) {
          spawnWave();
          nextWave = simT + WAVE_EVERY_S;
        }
        if (simT >= nextRelease) {
          const next = all
            .filter((t) => t.phase === 'inside' && !t.release && simT - t.t0 >= 0.2)
            .sort((a, b) => a.t0 - b.t0)
            .find(laneFree);
          if (next) {
            next.release = true;
            nextRelease = simT + RELEASE_EVERY_S;
          }
        }
        for (const t of all) step(t, g, z);
        for (let i = glows.length - 1; i >= 0; i--) {
          const glow = glows[i];
          const k = Math.min(1, (simT - glow.t0) / 0.7);
          const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
          trailAt(glow.fake, (d) => g.project(glow.walk.at(d * CANVAS_FIGMA_SCALE)), e * (glow.walk.length / CANVAS_FIGMA_SCALE), glow.z);
          if (k >= 1) {
            trailEnd(glow.fake);
            glows.splice(i, 1);
          }
        }
        for (let i = later.length - 1; i >= 0; i--) {
          if (simT < later[i].at) continue;
          const [due] = later.splice(i, 1);
          due.run();
        }
        for (let i = fading.length - 1; i >= 0; i--) {
          const f = fading[i];
          const k = (simT - f.t0) / TRAIL_FADE_S;
          if (k >= 1) {
            f.path.remove();
            fading.splice(i, 1);
          } else {
            f.path.style.opacity = String(1 - k);
            drawTrail(f.path, f.pts.map(g.project), z);
          }
        }
        if (dead.size) {
          tokens.current = all.filter((t) => !dead.has(t.id));
          const gone = new Set(dead);
          dead.clear();
          setTags((prev) => prev.filter((v) => !gone.has(v.id)));
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const trails = trailsRef.current;
    return () => {
      cancelAnimationFrame(raf);
      tokens.current = [];
      trails?.replaceChildren();
    };
  }, [geoRef, layer]);

  return createPortal(
    <div ref={layerRef} className={styles.layer} aria-hidden>
      <svg ref={trailsRef} className={styles.trails} />
      {tags.map((v) => (
        <div key={v.id} ref={register(v.id)} className={`${styles.tag}${v.mini ? ` ${styles.mini}` : ''}`}>
          <div className={styles.anchor}>
            <div className={styles.card}>
              <span className={`${styles.face} ${styles.front} sym-type-tiny-semi-bold`}>
                {v.mini ? (
                  <span className={styles.glyph}>{glyphFor(v.ch.slug)}</span>
                ) : (
                  <>
                    <ArrowRight size={14} strokeWidth={1.33} absoluteStrokeWidth aria-hidden />
                    <span className={styles.label}>{v.ch.label}</span>
                  </>
                )}
              </span>
              <span className={`${styles.face} ${styles.back} sym-type-tiny-semi-bold`}>
                <Check size={14} strokeWidth={1.33} absoluteStrokeWidth aria-hidden />
                {!v.mini && <span>{v.cat.label}</span>}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>,
    layer,
  );
}
